// src/hooks/useTaxiSocket.ts
import { BASE_URL } from '@/config';
import { useEffect } from 'react';
import { io, Socket } from 'socket.io-client';

let socket: Socket | null = null;
let currentSettingsId: number | null = null;

/**
 * Disconnects the taxi websocket connection
 * Should be called on logout to clean up the connection
 */
export function disconnectTaxiSocket() {
    if (socket) {
        console.log('🔌 Disconnecting taxi websocket');
        socket.removeAllListeners();
        socket.disconnect();
        socket = null;
        currentSettingsId = null;
        console.log('✅ Taxi websocket disconnected');
    }
}

export function useTaxiSocket(settingsId: number) {
    useEffect(() => {
        if ('Notification' in window) {
            console.log(`Connecting to taxi socket for settingsId: ${settingsId}`);

            // If socket exists but for a different settingsId, disconnect it first
            if (socket && currentSettingsId !== settingsId) {
                console.log(`🔄 Switching from settingsId ${currentSettingsId} to ${settingsId}, disconnecting old socket`);
                socket.removeAllListeners();
                socket.disconnect();
                socket = null;
            }

            // Only create a new socket if one doesn't exist or isn't connected
            if (!socket || !socket.connected) {
                if (Notification.permission !== 'granted') {
                    Notification.requestPermission().then((permission) => {
                        console.log('Notification permission:', permission);
                    });
                }

                currentSettingsId = settingsId;
                socket = io(`${BASE_URL}/taxi`, {
                    query: { /* optional auth */ },
                });

                // Wait for connection before joining room
                socket.on('connect', () => {
                    console.log('✅ Socket connected, joining room:', `operator_${settingsId}`);
                    socket?.emit('joinRoom', { room: `operator_${settingsId}` });
                });

                socket.on('disconnect', (reason) => {
                    console.log('❌ Socket disconnected:', reason);
                });

                socket.on('connect_error', (error) => {
                    console.error('❌ Socket connection error:', error);
                });

                // listen for driver responses
                socket.on('driverResponse', (data) => {
                    try {
                        // Append the new notification to the current list
                        if (Notification.permission === 'granted') {
                            new Notification('New Notification', {
                                body: data.status === 'ACCEPTED' ?
                                    `Driver has ${data.status} the request for ${data.taxiOrderId}` :
                                    `Driver has ${data.status} the request for ${data.taxiOrderId}`,
                            });
                        }
                    } catch (error) {
                        console.error('Error parsing SSE data:', error);
                    }
                    // show your modal here
                });

                socket.on("newOrder", (data) => {
                    console.log("Order data: ", data);
                    try {
                        if (Notification.permission === 'granted') {
                            new Notification("New Notification", {
                                body: "New order placed"
                            });
                        }
                    } catch (err) {
                        console.log(err)
                    }
                });

                socket.on('orderReminder', (data) => {
                    try {
                        if (Notification.permission === 'granted') {
                            new Notification(data.title || 'Order Reminder', {
                                body: data.message || `Reminder for order ${data.taxiOrderId}`,
                            });
                        } else {
                            console.warn('⚠️ Notification permission not granted');
                        }
                    } catch (error) {
                        console.error('❌ Error handling order reminder:', error);
                    }
                });
            }

            return () => {
                if (socket) {
                    socket.removeAllListeners();
                    socket.disconnect();
                }
            };
        } else {
            console.warn("Notifications not supported in the current browser")
        }
    }, [settingsId]);
}
